/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  FlowStream,
  DiscoveryIndex,
  TrafficHop,
  TrafficPath,
  MatchedRule,
  MatchedRoute,
  NetworkAcl,
  NACLEntry,
  SecurityGroupRule,
  Route,
} from './types';
import { isIPInCIDR, isIPInVPCCIDR, getProtocolName } from './parser';

// =============================================================================
// MAIN PATH ANALYSIS
// =============================================================================

/**
 * Analyze the complete traffic path for a flow stream.
 * Returns both inbound and outbound paths with all hops evaluated.
 */
export function analyzeTrafficPath(
  stream: FlowStream,
  index: DiscoveryIndex
): TrafficPath {
  const path: TrafficPath = {
    stream,
    inboundPath: [],
    outboundPath: [],
    verdict: 'allowed',
    deniedAt: undefined,
  };

  if (stream.direction === 'inbound' || stream.direction === 'unknown') {
    path.inboundPath = analyzeInboundPath(stream, index);
    const deniedHop = path.inboundPath.find(h => h.status === 'denied');
    if (deniedHop) {
      path.verdict = 'denied';
      path.deniedAt = deniedHop;
    }
  } else {
    path.outboundPath = analyzeOutboundPath(stream, index);
    const deniedHop = path.outboundPath.find(h => h.status === 'denied');
    if (deniedHop) {
      path.verdict = 'denied';
      path.deniedAt = deniedHop;
    }
  }

  // Set verdict based on actual flow action
  if (stream.action === 'REJECT') {
    path.verdict = 'denied';
  }

  return path;
}

// =============================================================================
// INBOUND PATH ANALYSIS
// =============================================================================

/**
 * Analyze inbound traffic path: External -> VPC -> Subnet -> NACL -> SG -> ENI
 */
export function analyzeInboundPath(
  stream: FlowStream,
  index: DiscoveryIndex
): TrafficHop[] {
  const path: TrafficHop[] = [];

  // 1. Identify the destination ENI
  const destEni = index.eniMap.get(stream.eniId);
  if (!destEni) {
    // Try to find ENI by destination IP
    const eniIdByIp = index.ipToEni.get(stream.dstAddr);
    if (!eniIdByIp) {
      path.push({
        type: 'ENI',
        id: stream.eniId,
        name: `Unknown ENI (${stream.eniId})`,
        details: { ip: stream.dstAddr },
        status: 'unknown',
      });
      return path;
    }
  }

  const eni = destEni || index.eniMap.get(index.ipToEni.get(stream.dstAddr) || '');
  if (!eni) return path;

  const destSubnet = index.subnetMap.get(eni.subnet_id);
  if (!destSubnet) return path;

  const vpcId = destSubnet.vpc_id || eni.vpc_id;
  const vpc = vpcId ? index.vpcMap.get(vpcId) : undefined;

  // 2. Determine entry point
  const srcIsInternal = isIPInVPCCIDR(stream.srcAddr, index);

  if (!srcIsInternal) {
    // External traffic - add external source
    path.push({
      type: 'EXTERNAL',
      id: 'external-source',
      name: 'Internet / External',
      details: { ip: stream.srcAddr, port: stream.srcPort },
      status: 'allowed',
    });

    // Determine entry gateway (IGW, NAT, TGW)
    const entryPoint = determineEntryPoint(stream, destSubnet, index);
    if (entryPoint) {
      path.push(entryPoint);
    }
  } else {
    // Internal traffic - add source ENI if we can find it
    const srcEniId = index.ipToEni.get(stream.srcAddr);
    if (srcEniId) {
      const srcEni = index.eniMap.get(srcEniId);
      path.push({
        type: 'ENI',
        id: srcEniId,
        name: srcEni?.description || srcEniId,
        details: { ip: stream.srcAddr, port: stream.srcPort },
        status: 'allowed',
      });
    }
  }

  // 3. Add VPC hop
  if (vpc) {
    path.push({
      type: 'VPC',
      id: vpcId || 'unknown-vpc',
      name: vpc.tags?.Name || vpcId || 'VPC',
      details: { cidr: vpc.cidr_block },
      status: 'allowed',
    });
  }

  // 4. Add Subnet hop
  path.push({
    type: 'SUBNET',
    id: destSubnet.id,
    name: destSubnet.tags?.Name || destSubnet.id,
    details: { cidr: destSubnet.cidr_block },
    status: 'allowed',
  });

  // 5. Evaluate NACL (inbound rules)
  const naclId = index.subnetToNacl.get(destSubnet.id);
  if (naclId) {
    const nacl = index.naclMap.get(naclId);
    if (nacl) {
      const naclResult = evaluateNACL(
        nacl,
        stream.srcAddr,
        stream.dstAddr,
        stream.dstPort,
        stream.protocol,
        false // inbound (Egress: false)
      );
      path.push({
        type: 'NACL',
        id: naclId,
        name: nacl.tags?.Name || naclId,
        details: { matchedRule: naclResult.rule || undefined },
        status: naclResult.allowed ? 'allowed' : 'denied',
      });

      if (!naclResult.allowed) {
        return path; // Traffic blocked at NACL
      }
    }
  }

  // 6. Evaluate Security Groups (inbound rules)
  // Get security groups - handle different formats
  const securityGroupIds: string[] = [];
  if (eni.security_groups && Array.isArray(eni.security_groups)) {
    for (const sg of eni.security_groups) {
      if (typeof sg === 'string') {
        securityGroupIds.push(sg);
      } else if (sg && typeof sg === 'object' && (sg as any).GroupId) {
        securityGroupIds.push((sg as any).GroupId);
      }
    }
  }
  // Also check Groups field (AWS API format)
  if ((eni as any).Groups && Array.isArray((eni as any).Groups)) {
    for (const g of (eni as any).Groups) {
      const gId = typeof g === 'string' ? g : g?.GroupId;
      if (gId && !securityGroupIds.includes(gId)) {
        securityGroupIds.push(gId);
      }
    }
  }

  console.log('[PathAnalyzer] ENI security groups:', eni.id, securityGroupIds);
  console.log('[PathAnalyzer] Available SGs in index:', Array.from(index.securityGroupMap.keys()));

  // Check if this is stateful return traffic (response to outbound connection)
  // Security Groups are stateful - return traffic is automatically allowed
  const isStatefulReturn = stream.isStatefulAllowed && stream.connectionType === 'response';
  
  if (isStatefulReturn) {
    console.log('[PathAnalyzer] Stateful return traffic detected - auto-allowed by SG');
    // For stateful return traffic, show the SG as allowing due to statefulness
    const firstSgId = securityGroupIds[0];
    const firstSg = firstSgId ? index.securityGroupMap.get(firstSgId) : undefined;
    path.push({
      type: 'SG',
      id: firstSgId || 'stateful',
      name: firstSg?.tags?.Name || firstSg?.group_name || firstSgId || 'Security Group',
      details: { 
        matchedRule: {
          action: 'allow',
          protocol: getProtocolName(stream.protocol),
          portRange: `${stream.srcPort}`,
          cidr: 'Stateful (return traffic)',
        }
      },
      status: 'allowed',
    });
  } else {
    // Normal SG rule evaluation for new inbound connections
    for (const sgId of securityGroupIds) {
      const sg = index.securityGroupMap.get(sgId);
      console.log('[PathAnalyzer] Looking up SG:', sgId, 'Found:', !!sg);
      if (sg) {
        const sgResult = evaluateSecurityGroup(
          sg.ip_permissions,
          stream.srcAddr,
          stream.dstPort,
          stream.protocol,
          index // Pass index for SG-to-SG rule evaluation
        );
        console.log('[PathAnalyzer] SG evaluation result:', sgId, sgResult);
        path.push({
          type: 'SG',
          id: sgId,
          name: sg.tags?.Name || sg.group_name || sgId,
          details: { matchedRule: sgResult.rule || undefined },
          status: sgResult.allowed ? 'allowed' : 'denied',
        });

        // Security groups are OR'd - if ANY allows, traffic passes
        if (sgResult.allowed) {
          break; // Found an allowing rule
        }
      }
    }
  }

  // If no SGs found, add a placeholder to show we looked
  if (securityGroupIds.length === 0) {
    console.warn('[PathAnalyzer] No security groups found for ENI:', eni.id);
    path.push({
      type: 'SG',
      id: 'no-sg-found',
      name: 'Security Group (not found)',
      details: {},
      status: 'unknown',
    });
  }

  // 7. Add destination ENI
  path.push({
    type: 'ENI',
    id: eni.id,
    name: eni.description || eni.id,
    details: { ip: stream.dstAddr, port: stream.dstPort },
    status: 'allowed',
  });

  return path;
}

// =============================================================================
// OUTBOUND PATH ANALYSIS
// =============================================================================

/**
 * Analyze outbound traffic path: ENI -> SG -> NACL -> Subnet -> Route -> Gateway -> External
 */
export function analyzeOutboundPath(
  stream: FlowStream,
  index: DiscoveryIndex
): TrafficHop[] {
  const path: TrafficHop[] = [];

  // 1. Identify the source ENI
  const srcEni = index.eniMap.get(stream.eniId);
  if (!srcEni) {
    const eniIdByIp = index.ipToEni.get(stream.srcAddr);
    if (!eniIdByIp) {
      path.push({
        type: 'ENI',
        id: stream.eniId,
        name: `Unknown ENI (${stream.eniId})`,
        details: { ip: stream.srcAddr },
        status: 'unknown',
      });
      return path;
    }
  }

  const eni = srcEni || index.eniMap.get(index.ipToEni.get(stream.srcAddr) || '');
  if (!eni) return path;

  const srcSubnet = index.subnetMap.get(eni.subnet_id);
  if (!srcSubnet) return path;

  const vpcId = srcSubnet.vpc_id || eni.vpc_id;

  // 2. Start with the source ENI
  path.push({
    type: 'ENI',
    id: eni.id,
    name: eni.description || eni.id,
    details: { ip: stream.srcAddr, port: stream.srcPort },
    status: 'allowed',
  });

  // 3. Evaluate Security Groups (outbound rules)
  let sgAllowed = false;
  for (const sgId of eni.security_groups || []) {
    const sg = index.securityGroupMap.get(sgId);
    if (sg) {
      const sgResult = evaluateSecurityGroup(
        sg.ip_permissions_egress,
        stream.dstAddr,
        stream.dstPort,
        stream.protocol,
        index // Pass index for SG-to-SG rule evaluation
      );
      path.push({
        type: 'SG',
        id: sgId,
        name: sg.tags?.Name || sg.group_name || sgId,
        details: { matchedRule: sgResult.rule || undefined },
        status: sgResult.allowed ? 'allowed' : 'denied',
      });

      if (sgResult.allowed) {
        sgAllowed = true;
        break;
      }
    }
  }

  if (!sgAllowed && (eni.security_groups?.length || 0) > 0) {
    return path; // Traffic blocked at SG
  }

  // 4. Evaluate NACL (outbound rules)
  const naclId = index.subnetToNacl.get(srcSubnet.id);
  if (naclId) {
    const nacl = index.naclMap.get(naclId);
    if (nacl) {
      const naclResult = evaluateNACL(
        nacl,
        stream.srcAddr,
        stream.dstAddr,
        stream.dstPort,
        stream.protocol,
        true // outbound (Egress: true)
      );
      path.push({
        type: 'NACL',
        id: naclId,
        name: nacl.tags?.Name || naclId,
        details: { matchedRule: naclResult.rule || undefined },
        status: naclResult.allowed ? 'allowed' : 'denied',
      });

      if (!naclResult.allowed) {
        return path; // Traffic blocked at NACL
      }
    }
  }

  // 5. Add Subnet hop
  path.push({
    type: 'SUBNET',
    id: srcSubnet.id,
    name: srcSubnet.tags?.Name || srcSubnet.id,
    details: { cidr: srcSubnet.cidr_block },
    status: 'allowed',
  });

  // 6. Evaluate Route Table
  const rtbId = index.subnetToRouteTable.get(srcSubnet.id);
  if (rtbId) {
    const rtb = index.routeTableMap.get(rtbId);
    if (rtb) {
      const routeMatch = findMatchingRoute(rtb.routes, stream.dstAddr);
      path.push({
        type: 'ROUTE_TABLE',
        id: rtbId,
        name: rtb.tags?.Name || rtbId,
        details: { matchedRoute: routeMatch },
        status: routeMatch.target === 'blackhole' ? 'denied' : 'allowed',
      });

      if (routeMatch.target === 'blackhole') {
        return path;
      }

      // 7. Follow the route to next hop
      const nextHopPath = followRoute(routeMatch, stream.dstAddr, index, vpcId || '');
      path.push(...nextHopPath);
    }
  }

  return path;
}

// =============================================================================
// NACL EVALUATION
// =============================================================================

interface NACLEvalResult {
  allowed: boolean;
  rule: MatchedRule | null;
}

/**
 * Evaluate NACL rules to determine if traffic is allowed.
 * NACL rules are evaluated in order by rule number (lowest first).
 */
export function evaluateNACL(
  nacl: NetworkAcl,
  srcAddr: string,
  dstAddr: string,
  port: number,
  protocol: number,
  isEgress: boolean
): NACLEvalResult {
  // Filter rules by direction
  const rules = nacl.entries
    .filter((e: NACLEntry) => e.Egress === isEgress)
    .sort((a: NACLEntry, b: NACLEntry) => a.RuleNumber - b.RuleNumber);

  const checkAddr = isEgress ? dstAddr : srcAddr;

  for (const rule of rules) {
    // Skip the default deny rule number (32767) until we've checked all others
    if (rule.RuleNumber === 32767) continue;

    // Check if CIDR matches
    if (!isIPInCIDR(checkAddr, rule.CidrBlock)) continue;

    // Check protocol (-1 = all protocols)
    if (rule.Protocol !== '-1' && parseInt(rule.Protocol, 10) !== protocol) continue;

    // Check port range (only for TCP/UDP)
    if ((protocol === 6 || protocol === 17) && rule.PortRange) {
      const { From, To } = rule.PortRange;
      if (port < From || port > To) continue;
    }

    // Rule matches!
    return {
      allowed: rule.RuleAction === 'allow',
      rule: {
        ruleNumber: rule.RuleNumber,
        action: rule.RuleAction as 'allow' | 'deny',
        protocol: rule.Protocol === '-1' ? 'All' : getProtocolName(parseInt(rule.Protocol, 10)),
        portRange: rule.PortRange ? `${rule.PortRange.From}-${rule.PortRange.To}` : 'All',
        cidr: rule.CidrBlock,
      },
    };
  }

  // Check the default deny rule (32767)
  const defaultDeny = rules.find((r: NACLEntry) => r.RuleNumber === 32767);
  if (defaultDeny && isIPInCIDR(checkAddr, defaultDeny.CidrBlock)) {
    return {
      allowed: false,
      rule: {
        ruleNumber: 32767,
        action: 'deny',
        protocol: 'All',
        portRange: 'All',
        cidr: defaultDeny.CidrBlock,
      },
    };
  }

  // No rule matched - implicit deny
  return { allowed: false, rule: null };
}

// =============================================================================
// SECURITY GROUP EVALUATION
// =============================================================================

interface SGEvalResult {
  allowed: boolean;
  rule: MatchedRule | null;
}

/**
 * Evaluate Security Group rules.
 * Security groups are allow-only with implicit deny.
 * Now supports:
 * - CIDR-based rules (IpRanges)
 * - SG-to-SG rules (UserIdGroupPairs)
 */
export function evaluateSecurityGroup(
  rules: SecurityGroupRule[],
  targetAddr: string,
  port: number,
  protocol: number,
  index?: DiscoveryIndex // Optional: needed for SG-to-SG rule evaluation
): SGEvalResult {
  // If we have the index, find which SGs the target IP belongs to
  let targetSgIds: string[] = [];
  if (index) {
    const targetEniId = index.ipToEni.get(targetAddr);
    if (targetEniId) {
      const targetEni = index.eniMap.get(targetEniId);
      if (targetEni) {
        targetSgIds = targetEni.security_groups || [];
      }
    }
  }

  for (const rule of rules) {
    // Check protocol (-1 = all protocols)
    if (rule.IpProtocol !== '-1') {
      const ruleProto =
        rule.IpProtocol === 'tcp' ? 6 :
        rule.IpProtocol === 'udp' ? 17 :
        rule.IpProtocol === 'icmp' ? 1 :
        parseInt(rule.IpProtocol, 10);
      if (ruleProto !== protocol) continue;
    }

    // Check port range (only for TCP/UDP)
    if ((protocol === 6 || protocol === 17) && rule.FromPort !== undefined) {
      if (port < rule.FromPort || port > (rule.ToPort || rule.FromPort)) continue;
    }

    // Check source/destination CIDR
    let cidrMatch = false;
    let matchedCidr = '';
    for (const ipRange of rule.IpRanges || []) {
      if (isIPInCIDR(targetAddr, ipRange.CidrIp)) {
        cidrMatch = true;
        matchedCidr = ipRange.CidrIp;
        break;
      }
    }

    if (cidrMatch) {
      return {
        allowed: true,
        rule: {
          action: 'allow',
          protocol: rule.IpProtocol === '-1' ? 'All' : rule.IpProtocol.toUpperCase(),
          portRange: rule.FromPort !== undefined ? `${rule.FromPort}-${rule.ToPort || rule.FromPort}` : 'All',
          cidr: matchedCidr,
        },
      };
    }

    // Check SG-to-SG rules (UserIdGroupPairs)
    // This allows traffic from/to ENIs that have the referenced SG attached
    if (rule.UserIdGroupPairs && rule.UserIdGroupPairs.length > 0 && targetSgIds.length > 0) {
      for (const sgRef of rule.UserIdGroupPairs) {
        if (targetSgIds.includes(sgRef.GroupId)) {
          return {
            allowed: true,
            rule: {
              action: 'allow',
              protocol: rule.IpProtocol === '-1' ? 'All' : rule.IpProtocol.toUpperCase(),
              portRange: rule.FromPort !== undefined ? `${rule.FromPort}-${rule.ToPort || rule.FromPort}` : 'All',
              cidr: `SG: ${sgRef.GroupId}`,
            },
          };
        }
      }
    }
  }

  // No rule matched - implicit deny
  return { allowed: false, rule: null };
}

// =============================================================================
// ROUTE MATCHING
// =============================================================================

/**
 * Find the best matching route using longest prefix match.
 */
export function findMatchingRoute(routes: Route[], destAddr: string): MatchedRoute {
  let bestMatch: Route | null = null;
  let bestPrefixLength = -1;

  for (const route of routes) {
    if (route.State !== 'active') continue;

    const dest = route.DestinationCidrBlock;
    if (!dest) continue;

    if (isIPInCIDR(destAddr, dest)) {
      const prefixLength = parseInt(dest.split('/')[1], 10);
      if (prefixLength > bestPrefixLength) {
        bestPrefixLength = prefixLength;
        bestMatch = route;
      }
    }
  }

  if (bestMatch) {
    const target =
      bestMatch.GatewayId ||
      bestMatch.NatGatewayId ||
      bestMatch.TransitGatewayId ||
      bestMatch.VpcPeeringConnectionId ||
      bestMatch.NetworkInterfaceId ||
      'unknown';
    return {
      destination: bestMatch.DestinationCidrBlock || '',
      target,
      targetType: getTargetType(target),
    };
  }

  // No route found - blackhole
  return {
    destination: '0.0.0.0/0',
    target: 'blackhole',
    targetType: 'blackhole',
  };
}

/**
 * Determine the type of route target.
 */
export function getTargetType(target: string): string {
  if (target === 'local') return 'local';
  if (target === 'blackhole') return 'blackhole';
  if (target.startsWith('igw-')) return 'igw';
  if (target.startsWith('nat-')) return 'nat';
  if (target.startsWith('tgw-')) return 'tgw';
  if (target.startsWith('pcx-')) return 'pcx';
  if (target.startsWith('eni-')) return 'eni';
  if (target.startsWith('vgw-')) return 'vgw';
  if (target.startsWith('vpce-')) return 'vpce';
  if (target.startsWith('eigw-')) return 'eigw';
  return 'unknown';
}

// =============================================================================
// ROUTE FOLLOWING
// =============================================================================

/**
 * Follow a route to its next hop and build the remaining path.
 */
export function followRoute(
  route: MatchedRoute,
  destAddr: string,
  index: DiscoveryIndex,
  vpcId: string,
  visited: Set<string> = new Set()
): TrafficHop[] {
  const path: TrafficHop[] = [];

  // Prevent infinite loops
  if (visited.has(route.target)) return path;
  visited.add(route.target);

  const targetType = route.targetType;

  switch (targetType) {
    case 'local':
      // Traffic stays within VPC - destination is in same VPC
      break;

    case 'igw': {
      const igw = index.igwMap.get(route.target);
      path.push({
        type: 'IGW',
        id: route.target,
        name: igw?.tags?.Name || route.target,
        details: {},
        status: 'allowed',
      });
      path.push({
        type: 'EXTERNAL',
        id: 'internet',
        name: 'Internet',
        details: { ip: destAddr },
        status: 'allowed',
      });
      break;
    }

    case 'nat': {
      const natGw = index.natGwMap.get(route.target);
      if (natGw) {
        path.push({
          type: 'NAT_GW',
          id: route.target,
          name: natGw.tags?.Name || route.target,
          details: {
            publicIp: natGw.nat_gateway_addresses?.[0]?.PublicIp,
            privateIp: natGw.nat_gateway_addresses?.[0]?.PrivateIp,
          },
          status: 'allowed',
        });

        // NAT GW is in a subnet - find its route table and continue
        if (natGw.subnet_id) {
          const natRtbId = index.subnetToRouteTable.get(natGw.subnet_id);
          if (natRtbId) {
            const natRtb = index.routeTableMap.get(natRtbId);
            if (natRtb) {
              const nextRoute = findMatchingRoute(natRtb.routes, destAddr);
              path.push({
                type: 'ROUTE_TABLE',
                id: natRtbId,
                name: natRtb.tags?.Name || natRtbId,
                details: { matchedRoute: nextRoute },
                status: 'allowed',
              });
              // Recursively follow the next route
              const nextPath = followRoute(nextRoute, destAddr, index, vpcId, visited);
              path.push(...nextPath);
            }
          }
        }
      }
      break;
    }

    case 'tgw': {
      path.push({
        type: 'TGW',
        id: route.target,
        name: `Transit Gateway ${route.target}`,
        details: {},
        status: 'allowed',
      });
      path.push({
        type: 'EXTERNAL',
        id: 'tgw-destination',
        name: 'TGW Network',
        details: { ip: destAddr },
        status: 'allowed',
      });
      break;
    }

    case 'pcx': {
      path.push({
        type: 'VPC_PEERING',
        id: route.target,
        name: `VPC Peering ${route.target}`,
        details: {},
        status: 'allowed',
      });
      path.push({
        type: 'EXTERNAL',
        id: 'peered-vpc',
        name: 'Peered VPC',
        details: { ip: destAddr },
        status: 'allowed',
      });
      break;
    }

    case 'vgw': {
      path.push({
        type: 'VGW',
        id: route.target,
        name: `VPN Gateway ${route.target}`,
        details: {},
        status: 'allowed',
      });
      path.push({
        type: 'EXTERNAL',
        id: 'vpn-network',
        name: 'VPN Network',
        details: { ip: destAddr },
        status: 'allowed',
      });
      break;
    }

    case 'vpce': {
      path.push({
        type: 'VPCE',
        id: route.target,
        name: `VPC Endpoint ${route.target}`,
        details: {},
        status: 'allowed',
      });
      break;
    }

    case 'blackhole': {
      path.push({
        type: 'EXTERNAL',
        id: 'blackhole',
        name: 'Blackhole (No Route)',
        details: { ip: destAddr },
        status: 'denied',
      });
      break;
    }
  }

  return path;
}

// =============================================================================
// ENTRY POINT DETERMINATION
// =============================================================================

/**
 * Determine how external traffic enters the VPC.
 */
function determineEntryPoint(
  stream: FlowStream,
  destSubnet: { id: string; vpc_id?: string },
  index: DiscoveryIndex
): TrafficHop | null {
  // Check if subnet has a route to IGW (public subnet)
  const rtbId = index.subnetToRouteTable.get(destSubnet.id);
  if (rtbId) {
    const rtb = index.routeTableMap.get(rtbId);
    if (rtb) {
      // Look for IGW route
      const igwRoute = rtb.routes.find(r =>
        r.GatewayId?.startsWith('igw-') && r.DestinationCidrBlock === '0.0.0.0/0'
      );
      if (igwRoute && igwRoute.GatewayId) {
        const igw = index.igwMap.get(igwRoute.GatewayId);
        return {
          type: 'IGW',
          id: igwRoute.GatewayId,
          name: igw?.tags?.Name || igwRoute.GatewayId,
          details: {},
          status: 'allowed',
        };
      }

      // Look for NAT Gateway (though inbound through NAT is uncommon)
      const natRoute = rtb.routes.find(r =>
        r.NatGatewayId && r.DestinationCidrBlock === '0.0.0.0/0'
      );
      if (natRoute && natRoute.NatGatewayId) {
        const natGw = index.natGwMap.get(natRoute.NatGatewayId);
        return {
          type: 'NAT_GW',
          id: natRoute.NatGatewayId,
          name: natGw?.tags?.Name || natRoute.NatGatewayId,
          details: {
            publicIp: natGw?.nat_gateway_addresses?.[0]?.PublicIp,
          },
          status: 'allowed',
        };
      }

      // Look for TGW
      const tgwRoute = rtb.routes.find(r =>
        r.TransitGatewayId && r.DestinationCidrBlock === '0.0.0.0/0'
      );
      if (tgwRoute && tgwRoute.TransitGatewayId) {
        return {
          type: 'TGW',
          id: tgwRoute.TransitGatewayId,
          name: `Transit Gateway ${tgwRoute.TransitGatewayId}`,
          details: {},
          status: 'allowed',
        };
      }
    }
  }

  // Default: assume IGW for public internet traffic
  return {
    type: 'IGW',
    id: 'inferred-igw',
    name: 'Internet Gateway (inferred)',
    details: {},
    status: 'allowed',
  };
}

